import { useState, useContext, useRef, useEffect } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import ApiHandlerContext from './ApiHandlerContext';
import HttpMethods from './HttpMethods';
import { executeHttpRequest } from './request-executor';
import {
  ApiError,
  ResponseState,
  UseRequestParameters,
  UseRequestState,
} from './types';
import {
  validateUrl,
  completeConfigWithCancellationTokenSource,
  replaceUrlPlaceholders,
  isSuccessResponse,
  isErrorResponse,
} from './utils';

interface RequestState<T> {
  data: T;
  error: ApiError;
  isLoading: boolean;
  status: number;
}

/**
 * This hook encapsulates the flow of HTTP requests, containing the response
 * data for both success and error scenarios, as well the information about if
 * the request is already finished or if it is still executing.
 *
 * Using this hook allows us to normalise our HTTP requests throughout all
 * applications.
 *
 * If used with ApiHandlerProvider and the app errors collection is passed to the
 * provider you will recieve the corresponding error if a request fails.
 *
 * When using one of the request methods, the isLoading value will be updated
 * to 'true' while the request is performing. When it finishes, isLoading will
 * be set to 'false' and one of the values (data or error) will be set with
 * the corresponding value.
 *
 * @param {string} requestMethod The HTTP method to execute
 *          (e.g. GET|POST|PUT|DELETE|PATCH).
 * @param {string} requestUrl The resource URL schema to make a request to.
 *          (e.g. '/cards/:cardId')
 */
function useRequest<T = any>(
  requestMethod: HttpMethods,
  requestUrl: string,
  parameters?: UseRequestParameters
): UseRequestState<T> {
  const [requestState, setRequestState] = useState<RequestState<T>>({
    data: null,
    error: null,
    isLoading: parameters?.executeImmediately || null,
    status: null,
  });

  const requestContext = useContext(ApiHandlerContext);
  const cancellationTokenSource = useRef(axios.CancelToken.source());

  validateUrl(requestUrl);

  useEffect(() => {
    if (parameters?.executeImmediately === true) execute();
  }, []);

  /**
   * This function controls the flow of the requests, updating the stateful
   * values when necessary.
   *
   * Before making the request, isLoading is set to true.
   *
   * After the request is finished, data and error are extracted from the HTTP
   * response and isLoading is set to false.
   *
   * @param {string} url The resource URL to make a request to.
   * @param {Record<string, any>} requestBody Data to send with the request.
   * @param {AxiosRequestConfig} config Axios config to pass with the request.
   *
   * @return {Promise<ResponseState>} An object containing the values of data and error
   *     resulting from the request.
   */
  async function execute(
    urlParameters?: Record<string, any>,
    requestBody?: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<ResponseState<T>> {
    const url = replaceUrlPlaceholders(requestUrl, urlParameters);

    if (!requestState.isLoading)
      setRequestState({ ...requestState, isLoading: true });

    const axiosConfig = completeConfigWithCancellationTokenSource(
      {
        ...requestContext.defaultConfig,
        ...config,
      },
      cancellationTokenSource.current
    );

    const response = await executeHttpRequest<T>(
      requestMethod,
      url,
      axiosConfig,
      requestBody
    );

    const responseError = isErrorResponse(response.status)
      ? response.error
      : null;

    setRequestState({
      data: response.data,
      error: responseError,
      isLoading: false,
      status: response.status,
    });

    return {
      ...response,
      error: responseError,
      isSuccess: isSuccessResponse(response.status),
      isError: isErrorResponse(response.status),
    };
  }

  /**
   * This function allows canceling a request that is currently executing.
   */
  function cancel(): void {
    cancellationTokenSource.current.cancel('Cancelled');
  }

  return {
    /**
     * A stateful value containing if a HTTP request is currently executing or
     * not. The value will be null before a request is fired.
     */
    isLoading: requestState.isLoading,
    /**
     * A stateful value containing the error details of the last request in
     * case of failure.
     */
    error: requestState.error,
    /**
     * A stateful value containing the response of the last request in case of
     * success.
     */
    data: requestState.data,
    /**
     * Executes the HTTP request.
     *
     * @param {Record<string, any>} urlParameters The values to replace the url params with.
     * @param {Record<string, any>} requestBody Data to send with the request.
     * @param {AxiosRequestConfig} config Axios config to pass with the request.
     */
    execute,
    /**
     * Cancels the HTTP request.
     */
    cancel,
    /**
     * True if response status is 4xx or 5xx
     */
    isError: isErrorResponse(requestState.status),
    /**
     * True if response status is 2xx
     */
    isSuccess: isSuccessResponse(requestState.status),
  };
}

export default useRequest;
